import { useContext, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { trim, flattenDeep, values, reverse, sortBy, sumBy, find, omitBy } from 'lodash'
import Money from '@/entities/Money'
import PayoutReport from '@/components/PayoutReport'
import { Context } from '@entities/Context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faCircleNotch, faCreditCard, faEdit, faFileInvoice, faPlusCircle, faTrash, faUniversity } from '@fortawesome/free-solid-svg-icons'

export default function Dashboard() {
    const { api, storage } = useContext(Context)
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [bankLoading, setBankLoading] = useState(false)
    const [error, setError] = useState(false)
    const [account, setAccount] = useState({})
    const [sources, setSources] = useState([])
    const [payments, setPayments] = useState([])

    // Helpers
    const money = (amount, decimals = 0, format) => {
        return (new Money(amount)).display(decimals, format)
    }

    const sortPayments = (payments) => {
        return values(reverse(sortBy(payments, (payment) => {
            return (new Date(payment.created)).valueOf()
        })))
    }

    // Computed values
    const canGetReports = useMemo(() =>
        account?.permissions?.includes('payout_reports') || false,
        [account]
    )

    const summarize = (key) => {
        return money(
            sumBy(payments, (payment) => {
                const line = find(payment.lines, { description: key })
                return payment.status === 'paid' && line ? line.amount : 0
            })
        )
    }

    const summaries = useMemo(() => ({
        tithes: summarize('Tithe'),
        missions: summarize('Missions'),
        building: summarize('Building'),
        special: money(
            sumBy(payments, (payment) => {
                const lines = omitBy(payment.lines, (line) => {
                    return ['Tithe', 'Missions', 'Building'].indexOf(line.description) >= 0
                })
                return sumBy(values(lines), 'amount')
            })
        )
    }), [payments])

    // Data fetching
    const getSources = async (accountId, pin) => {
        try {
            const { data } = await api.get('/api/source', { params: { account: accountId, pin } })
            setSources(data.data)
            storage.set('sources', data.data)
        } catch (error) {
            setError(trim(
                error.response.data.message +
                ' ' + flattenDeep(values(error.response.data.errors || {})).join(' ')
            ))
        }
    }

    const getPayments = async (accountId, pin) => {
        try {
            const { data } = await api.get('/api/payment', { params: { account: accountId, pin } })
            const sortedPayments = sortPayments(data.data)
            setPayments(sortedPayments)
            storage.set('payments', sortedPayments)
        } catch (error) {
            setError(trim(
                error.response.data.message +
                ' ' + flattenDeep(values(error.response.data.errors || {})).join(' ')
            ))
        }
    }

    const handleDeleteSource = async (source) => {
        if (loading) return

        if (window.confirm(`Are you sure you want to delete pay source ${source.brand} ****${source.last4} from your account?`)) {
            setLoading(true)
            setError(false)

            try {
                await api.delete(`/api/source/${source.id}`, {
                    params: {
                        account: storage.get('account').id,
                        pin: storage.get('pin'),
                    }
                })
                const newSources = values(omitBy(sources, { id: source.id }))
                storage.set('sources', newSources)
                setSources(newSources)
            } catch (error) {
                setError(trim(
                    error.response.data.message +
                    ' ' + flattenDeep(values(error.response.data.errors || {})).join(' ')
                ))
            } finally {
                setLoading(false)
            }
        }
    }

    async function ensureStripe() {
        if (!window.Stripe) {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script')
                script.src = 'https://js.stripe.com/v3/'
                script.async = true
                script.addEventListener('load', resolve)
                script.addEventListener('error', reject)
                document.body.appendChild(script)
            })
        }

        const key = document.head.querySelector('meta[name="stripe-key"]')

        return window.Stripe(key.content)
    }

    async function startBankSetupIntent() {
        const response = await api.post('/api/source', {
            type: 'bank',
            account: storage.get('account').id,
            pin: storage.get('pin'),
        })

        const secret = response?.data?.data?.client_secret
        if (!secret) throw new Error('Unable to setup bank account at this time.')

        return secret
    }

    async function triggerBankSetup() {
        if (bankLoading) return
        setBankLoading(true)
        setError(false)

        try {
            const pin = storage.get('pin')
            const acc = storage.get('account')
            if (!pin || !acc?.id) {
                navigate('/login', { replace: true })
                return
            }

            const stripe = await ensureStripe()
            const clientSecret = await startBankSetupIntent()

            const result = await stripe.collectBankAccountForSetup({
                clientSecret,
                params: {
                    payment_method_type: 'us_bank_account',
                    payment_method_data: {
                        billing_details: {
                            name: acc?.name || '',
                            email: acc?.email || undefined,
                        },
                    },
                },
                expand: ['payment_method'],
            })

            if (result?.error) throw new Error(result.error.message || 'Unable to connect bank account.')

            const setupIntent = result?.setupIntent
            if (!setupIntent) throw new Error('An error occurred with Stripe, please try again later.')

            if (setupIntent.status === 'requires_payment_method') {
                throw new Error('Bank account connection was canceled.')
            }

            if (setupIntent.status === 'requires_confirmation') {
                // const ok = window.confirm('Authorize this bank account for ACH payments and verification?')
                // if (!ok) throw new Error('Authorization was canceled.')

                const { setupIntent: confirmed, error: stripeError } = await stripe.confirmUsBankAccountSetup(clientSecret)

                if (stripeError) throw new Error(stripeError.message || 'Unable to confirm bank authorization.')
                if (!confirmed) throw new Error('An error occured with Stripe.')
            } else if (setupIntent.status !== 'succeeded') {
                throw new Error(`Unexpected bank setup status: ${setupIntent.status}`)
            }

            await getSources(acc.id, pin)
        } catch (error) {
            setError(trim(
                (error?.response?.data?.message || error?.message || 'Unknown error') +
                ' ' + flattenDeep(values(error?.response?.data?.errors || {})).join(' ')
            ))
        } finally {
            setBankLoading(false)
        }
    }

    // Navigation handlers
    const handleEditAccount = () => navigate('/account')
    const handleNewSource = () => navigate('/source')
    const handleNewPayment = () => navigate('/payment')

    // Initial data load
    useEffect(() => {
        const pin = storage.get('pin')
        if (!pin) {
            navigate('/login', { replace: true })
            return
        }

        const acc = storage.get('account')
        setAccount(acc)
        setSources(storage.get('sources'))
        setPayments(sortPayments(storage.get('payments')))

        getSources(acc.id, pin)
        getPayments(acc.id, pin)
    }, [])

    return (
        <div className="flex flex-wrap -mx-4">
            <div className="w-full px-4">
                {error && (
                    <div className="relative px-4 py-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded" role="alert">
                        <span className="block sm:inline">{error}</span>
                    </div>
                )}
            </div>

            {/* Left Column - Account Info & Sources */}
            <div className="w-full px-4 mb-6 md:mb-0 md:w-1/3">
                <p className="mb-4">
                    <strong className="text-lg font-bold truncate text-primary">
                        {account.name}
                    </strong>
                    <button
                        type="button"
                        onClick={handleEditAccount}
                        className="ml-3 text-center text-gray-500 focus:outline-none hover:text-primary"
                        title="Edit Account"
                    >
                        <FontAwesomeIcon icon={faEdit} className="fas" />
                    </button>
                    <br /> {account.email}
                </p>

                {/* Payment Sources */}
                {sources.map((source, index) => (
                    <div
                        key={source.id}
                        className={`flex flex-wrap items-start justify-between py-3 border-gray-300 source ${
                            index === 0 ? '' : 'border-t'
                        }`}
                    >
                        <span className="relative block w-full font-bold truncate text-primary">
                            {source.description || 'Primary Source'}
                            <button
                                type="button"
                                onClick={() => handleDeleteSource(source)}
                                className="absolute top-0 right-0 text-center text-gray-500 delete focus:outline-none hover:text-red-500"
                                title="Delete"
                            >
                                <FontAwesomeIcon icon={faTrash} className="fas" />
                            </button>
                        </span>
                        <span className="truncate">
                            {source.type === 'bank' ? (
                                <FontAwesomeIcon icon={faUniversity} className="mr-1 text-gray-500 fas" />
                            ) : (
                                <FontAwesomeIcon icon={faCreditCard} className="mr-1 text-gray-500 fas" />
                            )}
                            {source.brand} ****{source.last4}
                        </span>
                        {source.type !== 'bank' && (
                            <span className="text-center">
                            <span className="px-3 py-1 font-bold text-white uppercase bg-gray-500 rounded-full text-xxs">
                                {(source.expires || '').split('T')[0].substr(5).replace('-', '/')}
                            </span>
                        </span>
                        )}
                    </div>
                ))}

                {sources.length < 3 && (
                    <div className="flex flex-no-wrap mt-2 mb-4 grow">
                        <div className="w-1/2 mr-2">
                            <button
                                onClick={triggerBankSetup}
                                disabled={bankLoading}
                                className="w-full px-4 py-2 text-sm font-bold text-white whitespace-no-wrap rounded bg-primary"
                                type="button"
                            >
                                {bankLoading ? (
                                    <FontAwesomeIcon icon={faCircleNotch} spin className="fas" />
                                ) : (
                                    <FontAwesomeIcon icon={faUniversity} className="mr-2 fas" />
                                )}
                                {!bankLoading && 'Bank'}
                            </button>
                        </div>
                        <button
                            onClick={handleNewSource}
                            className="w-1/2 px-4 py-2 text-sm font-bold text-white whitespace-no-wrap rounded bg-primary"
                            type="button"
                        >
                            <FontAwesomeIcon icon={faCreditCard} className="mr-2 fas"/>
                            Card
                        </button>
                    </div>
                )}
            </div>

            <div className="w-full px-4 md:w-2/3">
                {sources.length > 0 && (
                    <button
                        onClick={handleNewPayment}
                        className="block px-4 py-2 mx-auto mb-6 text-sm font-bold text-white rounded sm:hidden bg-primary"
                        type="button"
                    >
                        <FontAwesomeIcon icon={faPlusCircle} className="mr-3 fas"/>
                        New Contribution
                    </button>
                )}

                {/* Statistics Grid */}
                <div className="flex flex-wrap -mx-2 text-center">
                    {[
                        { title: 'Tithes', value: summaries.tithes },
                        { title: 'Missions', value: summaries.missions },
                        { title: 'Building', value: summaries.building },
                        { title: 'Special', value: summaries.special }
                    ].map(({ title, value }) => (
                        <div key={title} className="w-full px-2 mb-4 sm:w-1/2">
                            <div className="py-2 bg-gray-200 rounded-t">
                                <h3 className="text-lg">{title}</h3>
                            </div>
                            <div className="py-2 border border-gray-200 rounded-b">
                                <p className="text-3xl font-bold truncate text-primary">{value}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-center w-full">
                    {sources.length > 0 && (
                        <button
                            onClick={handleNewPayment}
                            className="block px-4 py-2 my-4 mr-4 text-sm font-bold text-white rounded bg-primary"
                            type="button"
                        >
                            <FontAwesomeIcon icon={faPlusCircle} className="mr-3 fas" />
                            New Contribution
                        </button>
                    )}
                    {canGetReports && <PayoutReport onError={setError} />}
                </div>
            </div>

            {/* Payment History */}
            {payments.length > 0 && (
                <div className="w-full px-4 mt-2">
                    <h2 className="mb-2 text-2xl truncate">Contribution History</h2>
                    <div className="flex items-start justify-between py-2 -mx-6 border-b border-gray-300 sm:mx-0">
                        <span className="w-1/4 font-bold text-center sm:w-1/5">Number</span>
                        <span className="w-1/4 font-bold text-center sm:w-1/5">Date</span>
                        <span className="hidden font-bold text-center sm:block sm:w-1/5">Status</span>
                        <span className="w-1/4 font-bold text-center sm:w-1/5">Amount</span>
                        <span className="w-1/4 font-bold text-center sm:w-1/5">Receipt</span>
                    </div>
                    {payments.map(payment => (
                        <div key={payment.id} className="flex items-start justify-between py-3 -mx-6 border-t border-gray-300 sm:mx-0">
                            <span className="w-1/4 text-center sm:w-1/5">
                                {payment.number ? payment.number.split('-')[1] : null}
                            </span>
                            <span className="w-1/4 text-center whitespace-no-wrap sm:w-1/5">
                                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-gray-500 fas" />
                                {payment?.created.split('T')[0].split('-').slice(1).join('/')}
                            </span>
                            <span className="hidden text-center sm:block sm:w-1/5">
                                <span className={`px-3 py-1 font-bold text-white uppercase rounded-full text-xxs ${
                                    payment.status === 'paid' ? 'bg-green-500' : 'bg-orange-500'
                                }`}>
                                    {payment.status}
                                </span>
                            </span>
                            <span className="w-1/4 text-right sm:w-1/5">{money(payment.total, 2)}</span>
                            <a
                                className="w-1/4 text-center sm:w-1/5 text-primary hover:text-blue-400"
                                title="Download"
                                href={payment.receipt}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {payment.receipt && payment.status === 'paid' && (
                                    <FontAwesomeIcon icon={faFileInvoice} className="fas" />
                                )}
                            </a>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
